import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const CheckCircleIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MapPinIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const PhoneIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const InfoItem = ({ icon, children }) => (
  <li className="flex items-start gap-4">
    <div className="flex-shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-school-gold/10 text-school-gold mt-0.5">
      {icon}
    </div>
    <span className="text-gray-700 leading-relaxed pt-1.5">{children}</span>
  </li>
);

// ==================== MAIN PRINCIPAL MESSAGE COMPONENT ====================

const PrincipalMessage = () => {
  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Leadership
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                Principal's Message
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                A warm welcome from our school leadership
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== MAIN CONTENT ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">

              {/* Principal Photo */}
              <div className="lg:col-span-1">
                <div className="sticky top-40">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-brand-50 rounded-[2.25rem] -z-10 transform translate-x-4 translate-y-4" aria-hidden="true" />
                    <img
                      src="https://3.files.edl.io/c838/22/10/06/142646-486ebc89-b7d3-4c19-aeea-11e95526b0e3.jpg"
                      alt="Ms. Medina - Principal"
                      className="rounded-[2.25rem] shadow-soft w-full border border-gray-100"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-6 text-center">
                    <p className="text-2xl font-heading font-bold text-gray-900">Ms. Medina</p>
                    <p className="text-lg text-school-goldDark mt-1 font-medium">Principal</p>
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div className="lg:col-span-2">
                <div className="prose prose-lg max-w-none">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    <span className="font-semibold text-gray-900">Esteemed Families,</span>
                  </p>

                  <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-8">
                    Welcome to the 2023-2024 school year! We are thrilled to start a new year! I hope you all had an opportunity to rest and enjoy the summer. This year we have prepared to receive all students in the main building. Necessary upgrades were made to welcome our youngest scholars.
                  </p>

                  {/* Important Information Card */}
                  <div className="rounded-3xl border border-school-gold/20 p-8 md:p-10 my-10 shadow-soft" style={{ backgroundColor: '#EDE5D0' }}>
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-school-blue mb-6 tracking-tight">
                      Important Information
                    </h2>

                    <ul className="space-y-5">
                      <InfoItem icon={<CheckCircleIcon />}>
                        The first day of school for students is <strong className="text-gray-900">Thursday September 7, 2023</strong>
                      </InfoItem>

                      <InfoItem icon={<ClockIcon />}>
                        School hours remain as <strong className="text-gray-900">8:00 AM to 2:20 PM</strong>. Doors open for breakfast at 7:30 A.M.
                      </InfoItem>

                      <InfoItem icon={<MapPinIcon />}>
                        <div>
                          <p className="mb-3">Please use the <strong className="text-gray-900">flagpole entrance on 187th Street</strong> for breakfast for students in grades Pre-K and Kindergarten. (This area is newly designated for them)</p>
                          <p>Students in grades 1-5 will enter through the <strong className="text-gray-900">main entrance on Marion Ave</strong>. More information will be provided on arrival and dismissal for students.</p>
                        </div>
                      </InfoItem>

                      <InfoItem icon={<UserIcon />}>
                        We highly recommend students wear their school shirts for the first days of school. We will also be giving out school polo shirts to every student the first week.
                      </InfoItem>
                    </ul>
                  </div>

                  <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-8">
                    Families, we appreciate you and value that you've entrusted our team to this very important work in education. We look forward to another collaborative school year!
                  </p>

                  <div className="mt-10 pt-8 border-t border-gray-200">
                    <p className="text-lg font-heading font-bold text-gray-900">Ms. Medina</p>
                    <p className="text-school-goldDark font-medium">Principal</p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== CONTACT CTA ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Have Questions?
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              We're here to help! Reach out to our school office for any questions or concerns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+17185845275"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                <PhoneIcon />
                Call (718) 584-5275
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default PrincipalMessage;
